import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { riderApi } from '../api/riders';
import { isApiError, refusalMessage } from '../api/errors';
import { clearDriverSession, getDriverSession, isDriverSignedIn } from '../lib/driverSession';
import DriverPasswordField from '../components/driver/DriverPasswordField';
import './driverLoginPremium.css';

/**
 * The phone's registration for offers. At sign-in it was refused behind the
 * wall (403, which is never retried), so it is made again once the password
 * is chosen; it decides, and says, whether there is anything to do.
 */
function registerRiderPhone() {
  void import('../lib/driverPushBootstrap').then((m) => m.ensureDriverPushRegistered()).catch(() => {});
}

/**
 * A rider whose password came over from the old app chooses their own before
 * anything else opens (InGo D15; the server walls every other rider route
 * until then). The old password is asked again, as the console asks it: it is
 * never carried between screens. "Your old password", not "the one you signed
 * in with": a rider who came in with Google typed none.
 *
 * A rider who does not know it leaves signed out, for a reset link to their
 * own address: any new password lifts the wall. Signing out is open behind it.
 */
export default function DriverChoosePasswordPage({ rider = riderApi }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState({});
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  if (!isDriverSignedIn()) {
    return <Navigate to="/driver/login" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setProblem('');
    if (password !== confirmation) {
      setErrors({ confirmation: 'The two passwords are not the same.' });
      return;
    }
    setBusy(true);
    try {
      await rider.choosePassword(current, password);
      registerRiderPhone();
      navigate('/driver/home', { replace: true });
    } catch (error) {
      const fields = isApiError(error) ? error.errors : {};
      if (fields.current_password || fields.password) {
        setErrors({ current: fields.current_password?.[0], password: fields.password?.[0] });
      } else {
        setProblem(refusalMessage(error));
      }
    } finally {
      setBusy(false);
    }
  };

  /** Signed out on the server and here, then on to `to`. */
  const leave = async (to) => {
    setBusy(true);
    await rider.signOut();
    clearDriverSession();
    navigate(to, { replace: true });
  };

  const forgot = () => {
    const email = getDriverSession()?.email ?? '';
    void leave(`/forgot-password?realm=driver${email ? `&email=${encodeURIComponent(email)}` : ''}`);
  };

  return (
    <div className="dp-login-page">
      <main className="dp-login-body dp-login-body--alone">
        <div className="dp-login-card">
          <form className="dp-login-form" onSubmit={submit}>
            <h1 className="dp-login-title">Choose your password</h1>
            <p className="dp-login-subtitle">Your old password works once. Choose your own to continue.</p>

            {problem ? (
              <p className="dp-login-error" role="alert">
                {problem}
              </p>
            ) : null}

            <DriverPasswordField
              id="dcp-current"
              label="Your old password"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              error={errors.current}
              disabled={busy}
            />
            <DriverPasswordField
              id="dcp-new"
              label="New password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              error={errors.password}
              disabled={busy}
            />
            <DriverPasswordField
              id="dcp-confirm"
              label="New password again"
              value={confirmation}
              onChange={setConfirmation}
              autoComplete="new-password"
              error={errors.confirmation}
              disabled={busy}
            />

            <button type="submit" className="dp-login-btn" disabled={busy}>
              {busy ? 'Saving…' : 'Save and continue'}
            </button>
          </form>

          <p className="dp-login-customer-line">
            <button type="button" className="dp-login-customer-link dp-login-link-btn" onClick={forgot} disabled={busy}>
              Forgot your old password?
            </button>
          </p>
          <p className="dp-login-customer-line">
            <button type="button" className="dp-login-customer-link dp-login-link-btn" onClick={() => void leave('/driver/login')} disabled={busy}>
              Sign out
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
